/**
 * Onglet « Pièces adverses » — pièces communiquées par la partie
 * civile, le ministère public ou un co-prévenu.
 *
 * Concrétise le storage backend pieces_adverses (migration cc11dd22ee33) :
 * upload PDF + OCR sync à l'upload → contenu exploitable par le RAG/IA
 * pour neutraliser dans la réplique. Statuts : reçue / attendue /
 * contestée.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconEye, IconFileText, IconUpload, IconTrash, IconUsersGroup } from '@tabler/icons-react';
import Btn from './ui/Btn';
import PushToDriveButton from './PushToDriveButton';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

type PieceAdverse = {
  id: string;
  dossier_id: string;
  titre: string;
  date_piece: string | null;
  source: 'partie_civile' | 'ministere_public' | 'co_prevenu' | 'autre';
  author_label: string | null;
  statut: 'recue' | 'attendue' | 'contestee';
  has_file: boolean;
  filename_original: string | null;
  file_size_bytes: number | null;
  content_extracted_preview: string | null;
  conversion_status: 'pending' | 'processed' | 'failed' | 'unsupported' | 'skipped';
  conversion_error: string | null;
  ocr_status: 'pending' | 'processed' | 'failed';
  notes: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
};

const SOURCES: { code: PieceAdverse['source']; label: string }[] = [
  { code: 'partie_civile', label: 'Partie civile' },
  { code: 'ministere_public', label: 'Ministère public' },
  { code: 'co_prevenu', label: 'Co-prévenu' },
  { code: 'autre', label: 'Autre' },
];

const STATUTS: { code: PieceAdverse['statut']; label: string; color: string }[] = [
  { code: 'recue', label: 'Reçue', color: 'bg-cat-fav-pastel text-cat-fav' },
  { code: 'attendue', label: 'Attendue', color: 'bg-cat-imp-pastel text-cat-imp' },
  { code: 'contestee', label: 'Contestée', color: 'bg-cat-def-pastel text-cat-def' },
];

function formatBytes(n: number | null): string {
  if (n === null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

export default function PiecesAdversesTab({ dossierId }: { dossierId: string }) {
  const [pieces, setPieces] = useState<PieceAdverse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [titre, setTitre] = useState('');
  const [source, setSource] = useState<PieceAdverse['source']>('partie_civile');
  const [statut, setStatut] = useState<PieceAdverse['statut']>('recue');
  const [authorLabel, setAuthorLabel] = useState('');
  const [datePiece, setDatePiece] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.get<PieceAdverse[]>(`/dossiers/${dossierId}/pieces-adverses`);
      setPieces(rows);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const reset = () => {
    setTitre('');
    setSource('partie_civile');
    setStatut('recue');
    setAuthorLabel('');
    setDatePiece('');
    setNotes('');
    setFile(null);
    setOpen(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titre.trim() && !file) {
      toast.error('Indique au moins un titre OU un fichier.');
      return;
    }
    setBusy(true);
    try {
      if (file) {
        // Upload avec OCR sync (~5-30s pour un long PDF)
        const form = new FormData();
        form.append('file', file);
        form.append('titre', titre.trim() || file.name.replace(/\.[^.]+$/, ''));
        form.append('source', source);
        form.append('statut', statut);
        if (authorLabel.trim()) form.append('author_label', authorLabel.trim());
        if (datePiece) form.append('date_piece', datePiece);
        if (notes.trim()) form.append('notes', notes.trim());
        await api.postForm<PieceAdverse>(`/dossiers/${dossierId}/pieces-adverses/upload`, form);
      } else {
        // Mode métadonnée seule
        await api.post<PieceAdverse>(`/dossiers/${dossierId}/pieces-adverses`, {
          titre: titre.trim(),
          source,
          statut,
          author_label: authorLabel.trim() || null,
          date_piece: datePiece || null,
          notes: notes.trim() || null,
        });
      }
      reset();
      await reload();
      toast.success('Pièce adverse enregistrée');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (p: PieceAdverse) => {
    if (!confirm(`Supprimer définitivement « ${p.titre} » ?`)) return;
    try {
      await api.delete<void>(`/pieces-adverses/${p.id}`);
      await reload();
      toast.success('Pièce supprimée');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const onReconvert = async (p: PieceAdverse) => {
    try {
      await api.post<PieceAdverse>(`/pieces-adverses/${p.id}/reconvert`, {});
      await reload();
      toast.success('Reconversion lancée');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const quickUpload = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setBusy(true);
    try {
      let ok = 0;
      let err = 0;
      for (const f of list) {
        try {
          const form = new FormData();
          form.append('file', f);
          // Titre auto = filename sans extension.
          form.append('titre', f.name.replace(/\.[^.]+$/, ''));
          // Defaults — Maxim peut éditer après si besoin. L'IA pourra
          // déduire la source réelle via un follow-up Haiku sur le
          // content_extracted (TODO).
          form.append('source', 'autre');
          form.append('statut', 'recue');
          await api.postForm<PieceAdverse>(
            `/dossiers/${dossierId}/pieces-adverses/upload`,
            form
          );
          ok++;
        } catch (e) {
          console.error('quickUpload failed for', f.name, e);
          err++;
        }
      }
      await reload();
      if (err === 0) {
        toast.success(`${ok} pièce${ok > 1 ? 's' : ''} ajoutée${ok > 1 ? 's' : ''}`);
      } else if (ok === 0) {
        toast.error(`Échec sur les ${err} fichiers`);
      } else {
        toast.error(`${ok} OK, ${err} échec(s) — vérifie les noms`);
      }
    } finally {
      setBusy(false);
    }
  };

  const [dragOver, setDragOver] = useState(false);
  const quickInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-5 px-6 py-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">
            Conclusions et pièces adverses
          </h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Communiquées par la partie civile, le ministère public ou un co-prévenu. OCR page
            par page + indexation RAG (Voyage Law-2 + FTS) — exploitables comme les pièces du
            dossier répressif pour conclusions et briefing.
          </p>
        </div>
        <Btn variant="primary" onClick={() => setOpen((v) => !v)}>
          <IconUpload size={16} className="-ml-0.5 mr-1.5" />
          {open ? 'Annuler le formulaire' : 'Ajout détaillé'}
        </Btn>
      </header>

      {/* Drag-drop rapide — pas de formulaire, defaults source/statut */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void quickUpload(e.dataTransfer.files);
        }}
        onClick={() => quickInputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed p-6 text-[13px] transition ${
          dragOver
            ? 'border-accent bg-accent-light text-accent-text'
            : 'border-rule-strong bg-paper-elevated text-text-secondary hover:border-accent hover:bg-accent-light'
        }`}
        title="Drop des fichiers ici ou clique pour sélectionner — l'IA OCR-ise et indexe le RAG automatiquement"
      >
        <IconUpload size={28} stroke={1.6} />
        <p className="font-medium text-text-primary">
          {busy ? 'Upload en cours…' : 'Drop tes pièces adverses ici (ou clique)'}
        </p>
        <p className="text-[11.5px] text-text-tertiary">
          Multi-fichiers OK · OCR auto + RAG · tu pourras éditer la source/statut après depuis
          la liste.
        </p>
        <input
          ref={quickInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.xlsx,.xls,.ods,.msg,.eml,.jpg,.jpeg,.png,.tiff"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) void quickUpload(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {open && (
        <form
          onSubmit={onSubmit}
          className="space-y-3 rounded-lg border-[1.5px] border-dashed border-accent bg-accent-lighter p-4"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Titre
              </span>
              <input
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                placeholder="ex. Conclusions de la partie civile, conclusions principales"
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Source
              </span>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as PieceAdverse['source'])}
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              >
                {SOURCES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Statut
              </span>
              <select
                value={statut}
                onChange={(e) => setStatut(e.target.value as PieceAdverse['statut'])}
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              >
                {STATUTS.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Auteur (libellé)
              </span>
              <input
                value={authorLabel}
                onChange={(e) => setAuthorLabel(e.target.value)}
                placeholder="ex. Maître Dupont, conseil de M. Untel"
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Date de la pièce
              </span>
              <input
                type="date"
                value={datePiece}
                onChange={(e) => setDatePiece(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
            </label>
          </div>
          <label className="block">
            <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
              Fichier PDF (optionnel — sans, c'est un statut « attendue »)
            </span>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.xlsx,.xls,.ods,.msg,.eml,.jpg,.jpeg,.png,.tiff"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-1.5 block w-full text-sm"
            />
            {file && (
              <p className="mt-1 text-[11px] text-text-tertiary">
                L'OCR synchrone peut prendre 5-30s selon la taille du PDF.
              </p>
            )}
          </label>
          <label className="block">
            <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
              Notes (optionnel)
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
            />
          </label>
          <div className="flex justify-end">
            <Btn variant="primary" disabled={busy}>
              {busy ? (file ? 'Upload + OCR…' : 'Envoi…') : 'Enregistrer'}
            </Btn>
          </div>
        </form>
      )}

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-[13px] text-text-secondary">Chargement…</p>
      ) : pieces.length === 0 ? (
        <p className="rounded-md border border-dashed border-rule p-6 text-center text-[13px] text-text-secondary">
          Aucune pièce adverse pour l'instant.
        </p>
      ) : (
        <ul className="space-y-2">
          {pieces.map((p) => {
            const statut = STATUTS.find((s) => s.code === p.statut)!;
            const source = SOURCES.find((s) => s.code === p.source)!;
            return (
              <li key={p.id} className="rounded-md border border-rule bg-paper-elevated p-3">
                <div className="flex items-center gap-3">
                  <IconUsersGroup size={20} stroke={1.6} className="shrink-0 text-text-secondary" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-[13px] text-text-primary">{p.titre}</p>
                    <p className="mt-0.5 text-[11px] text-text-tertiary">
                      <span
                        className={`mr-1.5 rounded-pill px-1.5 py-[1px] font-medium ${statut.color}`}
                      >
                        {statut.label}
                      </span>
                      <span className="mr-1.5 rounded-pill bg-paper-subtle px-1.5 py-[1px] font-mono uppercase">
                        {source.label}
                      </span>
                      {p.author_label && <span>{p.author_label} · </span>}
                      {p.date_piece && (
                        <span>{new Date(p.date_piece).toLocaleDateString('fr-BE')} · </span>
                      )}
                      {p.has_file && (
                        <>
                          <IconFileText size={11} className="-mt-0.5 mr-0.5 inline-block" />
                          {formatBytes(p.file_size_bytes)} ·{' '}
                        </>
                      )}
                      uploadé {new Date(p.uploaded_at).toLocaleDateString('fr-BE')}
                    </p>
                  </div>
                  {p.has_file && (
                    <Link
                      to={`/dossiers/${dossierId}/pieces-adverses/${p.id}`}
                      title="Ouvrir dans le viewer (sélection texte + Re-OCR Mistral)"
                      className="inline-flex items-center gap-1 rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[11px] text-text-primary hover:bg-paper-subtle"
                    >
                      <IconEye size={13} stroke={1.6} />
                      Ouvrir
                    </Link>
                  )}
                  {p.has_file &&
                    (p.conversion_status === 'failed' ||
                      p.conversion_status === 'unsupported' ||
                      p.ocr_status === 'failed') && (
                      <button
                        type="button"
                        onClick={() => onReconvert(p)}
                        title="Relancer conversion + OCR"
                        className="rounded-md border border-cat-imp/40 bg-cat-imp-pastel px-2 py-1 text-[11px] text-cat-imp-text hover:bg-cat-imp/10"
                      >
                        Reconvertir
                      </button>
                    )}
                  {p.has_file && (
                    <PushToDriveButton
                      dossierId={dossierId}
                      kind="piece_adverse"
                      pieceId={p.id}
                      pieceTitle={p.titre}
                      variant="icon"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => onDelete(p)}
                    title="Supprimer"
                    className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-2 py-1 text-[11px] text-cat-def hover:bg-cat-def/10"
                  >
                    <IconTrash size={12} />
                  </button>
                </div>
                {p.has_file &&
                  (p.conversion_status === 'failed' || p.conversion_status === 'unsupported') && (
                    <p
                      className="mt-2 rounded bg-cat-def-pastel/40 px-2 py-1 text-[11.5px] text-cat-def"
                      title={p.conversion_error || undefined}
                    >
                      <strong>
                        Conversion{' '}
                        {p.conversion_status === 'unsupported' ? 'non supportée' : 'échouée'} :
                      </strong>{' '}
                      {p.conversion_error || 'erreur inconnue'}
                    </p>
                  )}
                {p.content_extracted_preview && (
                  <p className="mt-2 line-clamp-2 rounded bg-paper-subtle px-2 py-1 font-mono text-[10.5px] italic text-text-secondary">
                    OCR : {p.content_extracted_preview}…
                  </p>
                )}
                {p.notes && (
                  <p className="mt-1 text-[11px] italic text-text-secondary">{p.notes}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
